import { useEffect, useMemo, useState } from 'react';
import {
  arrayToSimpleObj,
  getPercentageValue,
  handleMentionData,
  sortTimeListFunc,
  supplementSingleData,
} from '@op/utils';
import { formatDateToServerDataFunc, getNeedTime } from '@op/utils/time';
import { getTopicAnalysisTimeline } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { IndicatorsType } from '@op/pages/Summary/context';

type TableDataType = {
  header: Array<any>;
  data: Array<any>;
};

function dealwithBlockData(data: { mentions: any; total: number }) {
  const { mentions, total } = data;
  return {
    data: (mentions || []).map((item: any) => ({
      name: item.key,
      count: item.value,
      child: item.child_topic_stats,
    })),
    total: total || 0,
  };
}

function dealwithTimeLineData(
  data: {
    timeline_of_start_time: any;
    timeline_of_end_time: any;
    timeline: any;
    sorts: any;
    sum: any;
  },
  filter: any,
  indicators: IndicatorsType,
) {
  const { timeline_of_start_time, timeline_of_end_time, timeline, sorts, sum } =
    data;

  const dateType = filter.date_type;
  const sortList = sorts || [];

  const list = supplementSingleData(
    timeline_of_start_time,
    timeline_of_end_time,
    timeline?.map((item: { count: any; value: any; key: any }) => ({
      ...item,
      count: item.count || item.value,
      name: item.key,
    })),
    dateType,
    sortList,
  );

  const needTime = getNeedTime(dateType);
  let { newArr, timeArr } = handleMentionData(list, needTime);
  newArr?.sort(
    (a: { name: any }, b: { name: any }) =>
      sortList?.indexOf(a.name) - sortList?.indexOf(b.name),
  );
  const sumMap = arrayToSimpleObj(sum, 'value', (item: { time: any }) =>
    formatDateToServerDataFunc(dateType, item.time),
  );

  if (
    [
      'mentions_ratio',
      'negative_mentions',
      'positive_mentions',
      'engagement_ratio',
    ].includes(indicators)
  ) {
    newArr = newArr.map((item: { data: any[] }) => {
      return {
        ...item,
        originData: item.data,
        data: item.data.map((value: any, i: number) => {
          return getPercentageValue(value, (sumMap as any)[timeArr[i]], 10);
        }),
      };
    });
  }

  const result: any = {
    timeline: { seriesDataArray: newArr || [], sumMap, xAxisData: timeArr },
    tableInfo: { header: [], data: [] },
  };

  const tableData: Array<any> = timeArr.map(
    (time: string | number, index: string | number) => {
      const itemObj: any = { time };
      newArr.forEach(({ data, name }: any) => {
        itemObj[name] = data[index];
      });
      itemObj['total'] = sumMap[time];
      return itemObj;
    },
  );
  tableData.sort(sortTimeListFunc('time', 'desc'));
  result['tableInfo'] = { header: sortList, data: tableData };

  return result;
}

export default function useTopicAnalysisTimeline({ filter }: { filter: any }) {
  const [indicators, setIndicators] = useState<IndicatorsType>('mentions');
  const [barAndLine, setBarAndLine] = useState({
    seriesDataArray: [],
    sumMap: {},
    xAxisData: [],
  });
  const [blockData, setBlockData] = useState({ data: [], total: 0 });
  const [apiError, setApiError] = useState(false);
  const [tableInfo, setTableInfo] = useState<TableDataType>({
    header: [],
    data: [],
  });

  const metricMap: any = {
    engagement_ratio: 'engagement',
    mentions_ratio: 'mentions',
  };

  const params = useMemo(() => {
    const metric = metricMap?.[indicators] || indicators;
    return {
      ...filter,
      metrics: [metric],
      sentiment_type: 'total',
      top_n: 20
    };
  }, [indicators, filter]);

  const { loading, wrapperRequest } = useCancelRequestHook();

  const requestTimeline = (requestParams: any) => {
    setApiError(false);
    setBarAndLine({ seriesDataArray: [], sumMap: {}, xAxisData: [] });
    setTableInfo({ header: [], data: [] });
    setBlockData({ data: [], total: 0 });
    wrapperRequest((cancelFunc: any) =>
      getTopicAnalysisTimeline(requestParams, cancelFunc).then((data: any) => {
        const { timeline, tableInfo } = dealwithTimeLineData(
          data,
          requestParams,
          indicators,
        );

        console.log('timeline :: ', timeline, tableInfo);
        const block = dealwithBlockData(data);
        setBarAndLine(timeline);
        setTableInfo(tableInfo);
        setBlockData(block);
      }),
    );
  };

  useEffect(() => {
    if (!params.edition_unified_id && !params.topic_analysis_task_id) return;
    requestTimeline(params);
  }, [params]);

  return {
    blockData,
    barAndLine,
    loading,
    apiError,
    tableInfo,
    indicators,
    setIndicators,
  };
}
